// API helpers for the employee Print Jobs overview + reassign surface
// (/api/employee/jobs/*). Like the rest of the employee surface, these call
// with the employee JWT in an explicit Authorization header (the default axios
// interceptor sends the customer access token; we override) — same pattern as
// searchEmployeeCustomers in api/impersonation.ts.

import type {
  EmployeeJobsQuery,
  EmployeeJobsResult,
  PortalCompanyDto,
  ReassignQuoteInput,
  ReassignTargetContactDto,
} from 'shared';

import { api } from './client.js';
import { getEmployeeAccessToken } from './impersonation.js';

// Mirrors the backend ReassignResult (services/employee/reassign.ts). Not in
// shared because it's an employee-internal response shape.
export type ReassignResultDto = {
  quote_id: string;
  company_id: string;
  contact_id: string;
  customer_user_id: string | null;
  matched_existing_user: boolean;
};

function employeeAuthHeader(): Record<string, string> {
  const token = getEmployeeAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function listJobs(query: EmployeeJobsQuery): Promise<EmployeeJobsResult> {
  // Strip undefined so blank filters don't become "undefined" query strings.
  const params: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '') {
      params[key] = value as string | number;
    }
  }
  const res = await api.get<EmployeeJobsResult>('/employee/jobs', {
    params,
    headers: employeeAuthHeader(),
  });
  return res.data;
}

// Companies the portal already knows (distinct company_id on existing quotes)
// — the only companies an orphan job may be filed under. `q` filters that
// portal-known set; it is NOT a Nexus directory search.
export async function listPortalCompanies(
  q?: string,
  opts?: { inviteLockedOnly?: boolean },
): Promise<PortalCompanyDto[]> {
  const params: Record<string, string> = {};
  if (q && q.trim().length > 0) params.q = q.trim();
  if (opts?.inviteLockedOnly) params.invite_locked_only = 'true';
  const res = await api.get<{ results: PortalCompanyDto[] }>('/employee/companies', {
    params,
    headers: employeeAuthHeader(),
  });
  return res.data.results;
}

// Contacts on a company, for the assign picker. `companyId` is only consulted
// for orphan jobs (the backend locks a filed job to its own company); pass the
// company the employee just picked so its contacts load.
export async function searchJobContacts(
  quoteId: string,
  q?: string,
  companyId?: string,
): Promise<ReassignTargetContactDto[]> {
  const params: Record<string, string> = {};
  if (q && q.trim().length > 0) params.q = q.trim();
  if (companyId) params.company_id = companyId;
  const res = await api.get<{ results: ReassignTargetContactDto[] }>(
    `/employee/jobs/${quoteId}/contacts`,
    { params, headers: employeeAuthHeader() },
  );
  return res.data.results;
}

export async function reassignJob(
  quoteId: string,
  payload: ReassignQuoteInput,
): Promise<ReassignResultDto> {
  const res = await api.post<ReassignResultDto>(
    `/employee/jobs/${quoteId}/reassign`,
    payload,
    { headers: employeeAuthHeader() },
  );
  return res.data;
}

// Soft-delete a job from the attribution worklist (status='deleted'). The
// cross-org admin counterpart to customer-side deleteQuote — works on any job,
// not just the caller's own. 204 on success.
export async function deleteJob(quoteId: string): Promise<void> {
  await api.delete(`/employee/jobs/${quoteId}`, { headers: employeeAuthHeader() });
}
